import { z } from 'zod';
import { type CodevScalarParameter, numberParameter, psfComputationParameters, psfComputationSettings, psfRowChanges, psfRowDescription, psfRowParameters, psfRowValues } from './shared.js';

const detectorRowValues = {
  ...psfRowValues,
  detector_x: z.number().nullable().optional(),
  detector_y: z.number().nullable().optional(),
};
const detectorRow = z.union([
  ...psfRowChanges.map(change => z.strictObject({ ...detectorRowValues, ...change })),
  z.strictObject({ ...detectorRowValues, detector_x: z.number() }),
  z.strictObject({ ...detectorRowValues, detector_y: z.number() }),
]);
const diffractionDetectorSettings = z.strictObject({
  ...psfComputationSettings,
  detector_x: z.number().describe('DEX: full circular detector diameter, or full X width with DEY, in lens units. A dimension is required because native DEX defaults to No (PromptingGuide p153).'),
  detector_y: z.number().nullable().default(null).describe('DEY: full Y width of a rectangular detector in lens units. Null uses native DEY No, giving a circular detector (PromptingGuide p153).'),
  option_settings: z.array(detectorRow).default([]).describe(`${psfRowDescription} Detector dimensions apply after the global DEX/DEY settings in row order. A blank detector cell leaves the current dimension unchanged (PromptingGuide p153).`),
});
const detectorRowParameters: readonly CodevScalarParameter[] = [
  ...psfRowParameters,
  { ...numberParameter('detector_x', 'Diameter / X width (lens units)', { blank: 'Unchanged' }), omit: { first: 'Global detector', later: 'Unchanged' } },
  { ...numberParameter('detector_y', 'Y width (lens units)', { blank: 'Unchanged' }), omit: { first: 'Global detector', later: 'Unchanged' } },
];

export default {
    label: 'Diffraction Detector Energy', group: 'performance',
    description: 'Diffraction energy collected by a circular or rectangular detector scanned across the image.',
    settingsSchema: diffractionDetectorSettings,
    parameters: [
      numberParameter('detector_x', 'Diameter / X width (lens units)', { prominent: true }),
      numberParameter('detector_y', 'Y width (lens units)', { blank: 'Circular detector' }),
      ...psfComputationParameters,
      { key: 'option_settings', label: 'Options', kind: 'rows', columns: detectorRowParameters },
    ],
    coverage: 'Maps are indexed by detector row and column. Relative energy and the chief-ray and maximum percentages use separate scales. Not supported: afocal systems, phase, wavefront perturbations, two-point-source convolution and intensity output.',
  } as const;
